import { useCallback } from 'react'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import type { OfferClickSearchParams } from '../contexts/ChatMessageActionsContext'
import { useDrawerSlot, type DrawerSlot } from './useDrawerSlot'

export interface PlaceDetails {
  readonly googlePlaceId: string
  readonly placeName: string
  readonly countryName?: string
  readonly stopId?: number
}

export interface InlinePlace {
  readonly googlePlaceId: string
  readonly placeName: string
}

export interface OfferDrawerData {
  readonly offerId: string
  readonly searchParams?: OfferClickSearchParams
  readonly offerType?: string
}

export interface UseDrawerStateReturn {
  detailPanel: DrawerSlot<PlaceDetails>
  placeInfo: DrawerSlot<InlinePlace>
  locationInfo: DrawerSlot<LocationInfoData>
  offerDrawer: DrawerSlot<OfferDrawerData>

  // Legacy API — thin wrappers kept for current consumers until PR 3 migrates them
  isDetailPanelOpen: boolean
  selectedPlaceDetails: PlaceDetails | undefined
  handleLearnMoreClick: (googlePlaceId: string, placeName: string, countryName: string, stopId?: number) => void
  handleDetailPanelDismiss: () => void
  handleDetailPanelAfterClose: () => void

  isPlaceInfoDrawerOpen: boolean
  selectedInlinePlace: InlinePlace | undefined
  handleInlinePlaceClick: (googlePlaceId: string, placeName: string) => void
  handlePlaceInfoDrawerClose: () => void
  handlePlaceInfoDrawerAfterClose: () => void

  isLocationInfoDrawerOpen: boolean
  selectedLocationInfo: LocationInfoData | undefined
  handleLocationInfoClick: (data: LocationInfoData) => void
  handleLocationInfoDrawerClose: () => void
  handleLocationInfoDrawerAfterClose: () => void

  resetDrawers: () => void
}

export function useDrawerState(): UseDrawerStateReturn {
  const detailPanel = useDrawerSlot<PlaceDetails>()
  const placeInfo = useDrawerSlot<InlinePlace>()
  const locationInfo = useDrawerSlot<LocationInfoData>()
  const offerDrawer = useDrawerSlot<OfferDrawerData>()

  const handleLearnMoreClick = useCallback((googlePlaceId: string, placeName: string, countryName: string, stopId?: number) => {
    detailPanel.open({ googlePlaceId, placeName, countryName, stopId })
  }, [detailPanel])

  const handleInlinePlaceClick = useCallback((googlePlaceId: string, placeName: string) => {
    placeInfo.open({ googlePlaceId, placeName })
  }, [placeInfo])

  const resetDrawers = useCallback(() => {
    detailPanel.reset()
    placeInfo.reset()
    locationInfo.reset()
    offerDrawer.reset()
  }, [detailPanel, placeInfo, locationInfo, offerDrawer])

  return {
    detailPanel,
    placeInfo,
    locationInfo,
    offerDrawer,

    // Legacy API
    isDetailPanelOpen: detailPanel.isOpen,
    selectedPlaceDetails: detailPanel.data,
    handleLearnMoreClick,
    handleDetailPanelDismiss: detailPanel.close,
    handleDetailPanelAfterClose: detailPanel.afterClose,

    isPlaceInfoDrawerOpen: placeInfo.isOpen,
    selectedInlinePlace: placeInfo.data,
    handleInlinePlaceClick,
    handlePlaceInfoDrawerClose: placeInfo.close,
    handlePlaceInfoDrawerAfterClose: placeInfo.afterClose,

    isLocationInfoDrawerOpen: locationInfo.isOpen,
    selectedLocationInfo: locationInfo.data,
    handleLocationInfoClick: locationInfo.open,
    handleLocationInfoDrawerClose: locationInfo.close,
    handleLocationInfoDrawerAfterClose: locationInfo.afterClose,

    resetDrawers,
  }
}
